
import React from 'react';
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { Application } from '@/types/database.types';

interface AdditionalPayrollComponentsProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

interface PayrollComponentProps {
  id: keyof Application;
  label: string;
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  detailsField: string;
  placeholder?: string;
}

const PayrollComponent = ({ 
  id, 
  label, 
  register, 
  setValue, 
  watch, 
  detailsField, 
  placeholder = "Please provide details..." 
}: PayrollComponentProps) => {
  const value = watch(id) as boolean | undefined;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id as string} className="font-medium text-foreground">
          {label}
        </Label>
        <Switch
          id={id as string}
          checked={value === true}
          onCheckedChange={(checked) => setValue(id, checked)}
        />
      </div>
      
      {value && (
        <div className="pl-5 border-l-2 border-primary/20 mt-2">
          <Textarea
            id={detailsField}
            {...register(detailsField as any)}
            placeholder={placeholder}
            className="mt-1"
          />
        </div>
      )}
    </div>
  );
};

export const AdditionalPayrollComponents = ({ register, setValue, watch }: AdditionalPayrollComponentsProps) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Additional Payroll Components</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Please indicate if your organization has any of the following additional payroll components:
        </p>
      </div>

      <div className="space-y-4">
        <PayrollComponent
          id="has_bonuses"
          label="Bonuses"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="bonus_structure_desc"
          placeholder="Please describe the bonus structure, frequency, eligibility criteria, and typical amounts..."
        />

        <PayrollComponent
          id="has_commission"
          label="Commissions"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="commission_structure_desc"
          placeholder="Please describe the commission structure, calculation method, payment schedule, and eligible roles..."
        />

        <PayrollComponent
          id="has_1099s"
          label="1099 Contractors"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="contractors_1099_desc"
          placeholder="Please describe the types of 1099 contractors, services provided, payment frequency, and typical amounts..."
        />

        <PayrollComponent
          id="has_k1s"
          label="K-1 Partners"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="partners_k1_desc"
          placeholder="Please describe the K-1 partnership structure, distribution schedule, and partnership details..."
        />

        <PayrollComponent
          id="has_clergy_pay"
          label="Clergy or Religious Pay"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="clergy_pay_desc"
          placeholder="Please describe the clergy/religious pay structure, frequency, eligibility, and any special tax considerations..."
        />
      </div>
    </div>
  );
};
